import { Bar, Doughnut } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend } from 'chart.js'
import type { Submission } from '../../lib/types'
import { count } from '../../lib/stats'
import { C } from '../../lib/colors'

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend)

const BASE = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }
const GRID_COLOR = 'rgba(1,87,98,0.07)'
const TIER_LABELS = ['Attuned', 'Listening In', 'Urgent Conv.', 'Priority Review']

interface Props { data: Submission[] }

export function Tiers({ data }: Props) {
  const tierCounts = [1, 2, 3, 4].map(t => count(data, d => d.tier === t))

  const bins    = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50]
  const binLabels = bins.slice(0, -1).map((v, i) => `${v}–${bins[i + 1] - 1}`)
  const binCounts = bins.slice(0, -1).map((v, i) => count(data, d => d.total_score >= v && d.total_score < bins[i + 1]))
  const binColors = bins.slice(0, -1).map(v => {
    if (v < 13) return C.tier[0]
    if (v < 29) return C.tier[1]
    if (v < 46) return C.tier[2]
    return C.tier[3]
  })

  return (
    <section className="sec" id="tiers">
      <div className="sec-hd">
        <div className="sec-label">02 · Tiers &amp; Scores</div>
        <h2 className="sec-title">How is clinical concern distributed?</h2>
        <p className="sec-sub">Four tiers: Attuned (1) · Listening In (2) · Urgent Conversation (3) · Priority Review (4). Score thresholds: ≤12 / ≤28 / ≤45 / &gt;45.</p>
      </div>
      <div className="g32">
        <div className="card">
          <div className="card-title">Score distribution (histogram)</div>
          <div className="card-sub">Total score in bins of 5 — coloured by tier range</div>
          <div className="cw h240">
            <Bar
              data={{ labels: binLabels, datasets: [{ data: binCounts, backgroundColor: binColors, borderRadius: 4 }] }}
              options={{ ...BASE, scales: { x: { grid: { display: false } }, y: { grid: { color: GRID_COLOR } } } }}
            />
          </div>
        </div>
        <div className="card">
          <div className="card-title">Tier composition</div>
          <div className="card-sub">% of cohort in each clinical tier</div>
          <div className="tier-legend">
            {C.tier.map((col, i) => (
              <div key={i} className="tl-item">
                <div className="tl-sw" style={{ background: col }} />
                Tier {i + 1} · {TIER_LABELS[i]}
              </div>
            ))}
          </div>
          <div className="cw h180">
            <Doughnut
              data={{ labels: TIER_LABELS, datasets: [{ data: tierCounts, backgroundColor: C.tier, borderWidth: 0, hoverOffset: 6 }] }}
              options={{ ...BASE, plugins: { legend: { display: true, position: 'bottom' as const, labels: { boxWidth: 10, padding: 10, font: { size: 10 } } } } }}
            />
          </div>
        </div>
      </div>
    </section>
  )
}
